import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = 'notifications-off'

// Fires browser notifications when a task's start time is reached.
// Requires the tab to be open. Checks every 30 seconds.
// Also registers a Web Push subscription so notifications work when the browser is closed.
export default class extends Controller {
  static values = { tasks: Array, title: String }
  static targets = ["button"]

  connect() {
    this._notified = new Set()
    this._connectTime = Date.now()
    this._disabled = localStorage.getItem(STORAGE_KEY) === 'true'

    // Pre-mark tasks that started more than 60 seconds before page load — too old to notify.
    const graceMs = 60 * 1000
    this.tasksValue.forEach(task => {
      if (!task.start_time) return
      const startMs = new Date(task.start_time).getTime()
      if (startMs < this._connectTime - graceMs) {
        this._notified.add(task.id)
      }
    })

    this._updateButton()
    this._tick()
    this._interval = setInterval(() => this._tick(), 30_000)
  }

  disconnect() {
    clearInterval(this._interval)
  }

  async toggle() {
    if (typeof Notification === 'undefined') return

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return
      this._disabled = false
      localStorage.removeItem(STORAGE_KEY)
      await this._subscribePush()
    } else {
      this._disabled = !this._disabled
      if (this._disabled) {
        localStorage.setItem(STORAGE_KEY, 'true')
        await this._unsubscribePush()
      } else {
        localStorage.removeItem(STORAGE_KEY)
        await this._subscribePush()
      }
    }

    this._updateButton()
    this._tick()
  }

  async _subscribePush() {
    try {
      const registration = await navigator.serviceWorker.ready
      const vapidPublicKey = document.querySelector('meta[name="vapid-public-key"]').content
      const applicationServerKey = this._urlBase64ToUint8Array(vapidPublicKey)

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      })

      const { endpoint, keys } = subscription.toJSON()
      await fetch('/push_subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ endpoint, p256dh: keys.p256dh, auth: keys.auth })
      })
    } catch (e) {
      console.error('[PushSubscription] subscribe failed:', e)
    }
  }

  async _unsubscribePush() {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) return

      await fetch('/push_subscription', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      })

      await subscription.unsubscribe()
    } catch (e) {
      console.error('[PushSubscription] unsubscribe failed:', e)
    }
  }

  _urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)))
  }

  _updateButton() {
    if (!this.hasButtonTarget) return
    const granted = typeof Notification !== 'undefined' && Notification.permission === 'granted'
    const active = granted && !this._disabled
    this.buttonTarget.style.color = active ? '#2563eb' : ''
    const bellOn  = this.buttonTarget.querySelector('[data-bell-on]')
    const bellOff = this.buttonTarget.querySelector('[data-bell-off]')
    if (bellOn)  bellOn.classList.toggle('hidden', !active)
    if (bellOff) bellOff.classList.toggle('hidden', active)
    const text = active
      ? this.buttonTarget.dataset.labelGranted
      : this.buttonTarget.dataset.labelDefault
    const label = this.buttonTarget.querySelector('[data-notifier-label]')
    if (label) {
      label.textContent = text
    } else {
      this.buttonTarget.textContent = text
    }
  }

  _playChime() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const notes = [783.99, 987.77, 1174.66] // G5, B5, D6 — pleasant major chord arpeggio

      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)

        osc.type = 'sine'
        osc.frequency.value = freq

        const start = ctx.currentTime + i * 0.18
        const end = start + 0.4

        gain.gain.setValueAtTime(0, start)
        gain.gain.linearRampToValueAtTime(0.3, start + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, end)

        osc.start(start)
        osc.stop(end)
        osc.close()
      })
    } catch (e) {
      // Audio not supported — fail silently
    }
  }

  _tick() {
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') return
    if (this._disabled) return

    const now = Date.now()

    this.tasksValue.forEach(task => {
      if (this._notified.has(task.id)) return
      if (!task.start_time) return

      const startMs = new Date(task.start_time).getTime()
      if (now >= startMs) {
        try {
          new Notification(this.titleValue, {
            body: task.description,
            icon: '/icon.png',
            tag: `task-${task.id}`
          })
          this._playChime()
          this._notified.add(task.id)
        } catch (e) {
          console.error('[Notifier] notification failed:', e)
        }
      }
    })
  }
}
